import React, { useState, useEffect } from "react";
import {
  FaPlay,
  FaCheckCircle,
  FaHourglassHalf,
  FaChevronDown,
  FaChevronRight,
} from "react-icons/fa";
import { toast } from "react-hot-toast";

const ModuleSidebar = ({
  course,
  module,
  selectedSubsection,
  completedSubs,
  isPreviewUser = false,
  onSelectModule,
  onSelectSubsection,
  onReturnToLearning,
  onGoToActionPlan,
}) => {
  const [openSection, setOpenSection] = useState(null);

  // Group subsections by section
  const grouped =
    module?.subsections?.reduce((acc, sub) => {
      const section = sub.section || "Other";
      if (!acc[section]) acc[section] = [];
      acc[section].push(sub);
      return acc;
    }, {}) || {};

  // Flatten all subsections (ordered) to enable next navigation
  const allSubs =
    course?.modules
      ?.sort((a, b) => a.order - b.order)
      ?.flatMap((m) =>
        m.subsections
          ?.sort((a, b) => a.order - b.order)
          ?.map((s) => ({ ...s, moduleId: m.id, module: m }))
      ) || [];

  const currentIndex = allSubs.findIndex(
    (s) => s.id === selectedSubsection?.id
  );

  const currentSubsection = allSubs[currentIndex];
  const nextSubsection = allSubs[currentIndex + 1];
  const isLastSectionInModule =
    currentSubsection &&
    nextSubsection &&
    currentSubsection.moduleId !== nextSubsection.moduleId;

  // const isSubsectionCompleted = (sub) =>
  //   sub?.status === "completed" ||
  //   sub?.completed === true ||
  //   completedSubs?.[sub?.id] === true;

  // Auto-expand the active section
  useEffect(() => {
    if (!selectedSubsection) return;
    const activeSection = selectedSubsection.section || "Other";
    setOpenSection(activeSection);
  }, [selectedSubsection]);

  const notifyIncomplete = (message) =>
    toast.error(
      message ||
        "This section isn't complete yet. You can continue, but come back to finish it."
    );

  const isSubsectionCompleted = (sub) =>
    sub?.status === "completed" ||
    sub?.completed === true ||
    completedSubs?.[sub?.id] === true;

  const isModuleCompleted = (mod) =>
    mod?.subsections?.length
      ? mod.subsections.every((sub) => isSubsectionCompleted(sub))
      : false;

  const isModuleUnlocked = (mod) => {
    if (!course?.modules?.length || !mod) return false;
    if (typeof mod.is_unlocked === "boolean") {
      return mod.is_unlocked;
    }
    const ordered = [...course.modules].sort((a, b) => a.order - b.order);
    const idx = ordered.findIndex((m) => m.id === mod.id);
    if (isPreviewUser && (mod.order || idx + 1) > 1) return false;
    if (idx <= 0) return true;
    return ordered.slice(0, idx).every((m) => isModuleCompleted(m));
  };

  const handleNextLesson = () => {
    if (currentIndex === -1) return;

    if (currentIndex === allSubs.length - 1) {
      onGoToActionPlan?.();
      return;
    }

    if (
      selectedSubsection?.type === "ai" &&
      !isSubsectionCompleted(selectedSubsection)
    ) {
      notifyIncomplete("Chat is still in progress. Mark it complete when you're done.");
    } else if (selectedSubsection?.status === "in_progress") {
      notifyIncomplete();
    }

    const currentSub = allSubs[currentIndex];
    const nextSub = allSubs[currentIndex + 1];

    const movingToNextModule =
      nextSub && currentSub?.moduleId !== nextSub.moduleId;
    if (movingToNextModule) {
      if (!isModuleCompleted(currentSub?.module)) {
        toast.error("Finish this module to unlock the next one.");
        return;
      }
      if (!isModuleUnlocked(nextSub?.module)) {
        toast.error("Complete previous modules to unlock this one.");
        return;
      }
      onReturnToLearning?.(nextSub?.module);
      return;
    }

    onSelectModule?.(nextSub.module);
    onSelectSubsection?.(nextSub);
  };

  const handleSubsectionClick = (sub) => {
    if (sub.id !== selectedSubsection?.id) {
      if (
        selectedSubsection?.type === "ai" &&
        !isSubsectionCompleted(selectedSubsection)
      ) {
        notifyIncomplete("Chat is still in progress. Mark it complete when you're done.");
      } else if (selectedSubsection?.status === "in_progress") {
        notifyIncomplete();
      }
    }
    onSelectSubsection?.(sub);
  };

  const toggleSection = (sectionName) => {
    setOpenSection((prev) => (prev === sectionName ? null : sectionName));
  };

  if (!module?.subsections?.length) {
    return (
      <div className="p-6 text-gray-400 bg-white rounded-xl shadow-sm border">
        No subsections available
      </div>
    );
  }

  return (
    <aside className="bg-gray-100 overflow-hidden">
      {/* Header */}
      <div className="flex justify-between items-center mb-4 px-5 pt-5">
        <h3 className="text-black text-xl">Your Progress</h3>
        <span className="text-sm text-gray-400">
          {currentIndex + 1}/{allSubs.length}
        </span>
      </div>

      {/* Scrollable Section List */}
      <div className="flex-1 overflow-y-auto px-5 pb-5 bg-white rounded-2xl shadow-sm border scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent">
        {Object.entries(grouped).map(([sectionName, subs], i, arr) => (
          <div
            key={sectionName}
            className={`relative ${
              i < arr.length - 1 ? "border-b border-gray-100 pb-3 mb-3" : ""
            }`}
          >
            {/* Section Header */}
            <button
              onClick={() => toggleSection(sectionName)}
              className="flex items-center justify-between w-full text-left text-gray-800 font-semibold text-sm py-2"
            >
              <span>
                {i + 1}. {sectionName}
              </span>
              <FaChevronDown
                className={`transition-transform ${
                  openSection === sectionName ? "rotate-180" : "rotate-0"
                } text-gray-400`}
                aria-hidden
              />
            </button>

            {/* Section Items */}
            <div
              className={`transition-all duration-300 overflow-hidden ${
                openSection === sectionName
                  ? "max-h-[600px] opacity-100"
                  : "max-h-0 opacity-0"
              }`}
            >
              <div className="relative pl-[26px]">
                {/* Vertical line through circles */}
                <div className="absolute left-[34px] top-[4px] bottom-[4px] w-[2px] bg-gray-100 pointer-events-none" />

                <ul className="space-y-1">
                  {subs.map((sub) => {
                    const isActive = sub.id === selectedSubsection?.id;
                    const isCompleted =
                      sub.status === "completed" ||
                      sub.completed === true ||
                      completedSubs?.[sub.id] === true;
                    const isInProgress = sub.status === "in_progress";

                    return (
                      <li
                        key={sub.id}
                        onClick={() => handleSubsectionClick(sub)}
                        className={`relative flex items-start gap-3 py-2.5 pr-3 cursor-pointer transition-colors ${
                          isActive
                            ? "text-[#054A52] font-medium"
                            : "text-gray-700 hover:bg-gray-50"
                        }`}
                      >
                        {/* Circle on the vertical line */}
                        <div
                          className={`relative z-10 w-[18px] h-[18px] rounded-full flex items-center justify-center shrink-0 self-start mt-[2px] ${
                            isCompleted
                              ? "bg-[#4EB7B4]"
                              : isInProgress
                              ? "bg-yellow-400"
                              : isActive
                              ? "bg-[#54B0AF]"
                              : "bg-gray-300"
                          }`}
                        >
                          {isCompleted ? (
                            <FaCheckCircle className="text-white text-[9px]" />
                          ) : isInProgress ? (
                            <FaHourglassHalf className="text-white text-[9px]" />
                          ) : (
                            <FaPlay className="text-white text-[9px] ml-[1px]" />
                          )}
                        </div>

                        {/* Title */}
                        <span
                          className={`text-[13.5px] leading-snug whitespace-normal break-words flex-1 ${
                            isActive ? "text-[#54B0AF]" : "text-gray-700"
                          }`}
                        >
                          {sub.title}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="p-5 mt-auto rounded-b-2xl">
        <button
          id="next-lesson-btn"
          onClick={handleNextLesson}
          className="w-full flex items-center justify-between rounded-[20px] bg-[#54B0AF] px-[20px] py-[22px] text-white font-gotham font-medium text-[18px] md:text-[21px] leading-none hover:bg-[#4AA5B3] transition-all"
        >
                  <span className="whitespace-nowrap">
                    {isLastSectionInModule ? "Next Module" : "Next Section"}
                  </span>

          {/* Chevron in white circle */}
          <div className="w-[28px] h-[28px] rounded-full bg-white flex items-center justify-center">
            <FaChevronRight
              className="text-[#54B0AF] w-[14px] h-[14px]"
              aria-hidden
            />
          </div>
        </button>
      </div>
    </aside>
  );
};

export default ModuleSidebar;
